import { useEffect, useState } from 'react';
import {
  regionPoint,
  REGIONS,
  BODY_TYPE_LABEL,
  TOWED_KINDS,
  manages,
  type AvailabilityRow,
  type BodyType,
  type FleetRow,
  type MyCompanyRow,
} from 'shared';
import { api, type Me } from '../api';
import { useFormErrors } from '../useFormErrors';
import FieldError from '../FieldError';
import BodyTypeSelect from '../BodyTypeSelect';
import { BodyTypeOne } from '../BodyTypePicker';
import NumberField from '../NumberField';
import RegionPicker from '../RegionPicker';
import FleetMatch from './FleetMatch';
import { alert, confirm, notify, openTelegram } from '../telegram';

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n: number) =>
  new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

/**
 * Свободные машины: перевозчик объявляет, где освобождается, заказчик
 * ищет попутку под обратную загрузку.
 *
 * Порожний пробег — главная статья убытка перевозчика, и закрыть её
 * может только площадка: по телефону такую машину не найти.
 */
export default function Trucks({ onOpenCargo }: { onOpenCargo: (id: string) => void }) {
  const [items, setItems] = useState<AvailabilityRow[]>([]);
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Весь парк управляемой компании, не только машины, заведённые лично
  // публикующим диспетчером, — иначе технику, которую в парк добавил другой
  // сотрудник или сам водитель, из формы не выбрать вовсе.
  const [fleet, setFleet] = useState<FleetRow[]>([]);
  // НЕ `me.company` — это отдельное поле (`User.companyId`, привязка по ИНН
  // в профиле), пустое у диспетчера, зашедшего по инвайт-коду. Место в
  // компании, включая право распоряжаться, живёт в `CompanyMember`, и
  // единственный источник правды по нему — этот список (тот же, что уже
  // использует Company.tsx), а не пара `me.company`+`me.managesCompany`.
  const [companies, setCompanies] = useState<MyCompanyRow[]>([]);
  const managedCompany = companies.find((c) => manages(c.roles)) ?? null;

  const [tab, setTab] = useState<'all' | 'mine' | 'match'>('all');
  const [fRegion, setFRegion] = useState('');
  const [fBody, setFBody] = useState<BodyType | ''>('');

  const [showForm, setShowForm] = useState(false);
  const errs = useFormErrors();
  const [form, setForm] = useState({
    vehicleId: '',
    fromRegion: REGIONS[0] as string,
    toRegion: '',
    bodyType: 'TENT' as BodyType,
    capacityT: 20,
    availableFrom: today(),
    availableTo: inDays(3),
    note: '',

    /*
     * Свободное место в рейсе. Пусто — обычное объявление «машина свободна»;
     * заполнено — заявка на догруз, и по ней подбираются попутные грузы.
     */
    freeT: '',
    freeLengthM: '',
    detourKm: '50',
  });

  async function load() {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([
        api.availability({
          mine: tab === 'mine' ? 'true' : undefined,
          fromRegion: fRegion || undefined,
          bodyType: fBody || undefined,
        }),
        me ? Promise.resolve(me) : api.me(),
      ]);
      setItems(a.items);
      setMe(m);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, fRegion, fBody]);

  // Не по каждой смене фильтра ленты, а один раз — как только известно,
  // какими компаниями человек распоряжается. Новый диспетчер (в том числе
  // зашедший по инвайт-коду), новая компания, новая машина в парке —
  // ничего здесь дописывать не нужно: источник тот же список мест, что и
  // у остального парка.
  useEffect(() => {
    void api.myCompanies().then((r) => setCompanies(r.items)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!managedCompany) return;
    api
      .fleet(managedCompany.id)
      .then((r) => setFleet(r.items))
      .catch(() => {});
  }, [managedCompany?.id]);

  // Своя машина — всегда (частник без компании публикует только её).
  // Тем, кто распоряжается парком компании, добавляется весь её парк —
  // независимо от того, кто конкретно завёл каждую единицу техники.
  // Прицепы и полуприцепы из выбора исключены: сами по себе они груз не
  // возят, «свободна» у них означает не то же самое, что у тягача.
  const notTowed = (kind: FleetRow['kind'] | undefined) => !kind || !TOWED_KINDS.includes(kind);
  const vehicleOptions = me && managedCompany
    ? Array.from(
        new Map(
          [...me.vehicles, ...fleet.filter((v) => v.active)]
            .filter((v) => notTowed(v.kind))
            .map((v) => [v.id, v]),
        ).values(),
      )
    : (me?.vehicles ?? []).filter((v) => notTowed(v.kind));

  async function submit() {
    const bad: Record<string, string> = {};

    if (new Date(form.availableTo) < new Date(form.availableFrom)) {
      bad.availableTo = 'Дата окончания раньше начала';
    }
    // Догруз без «куда» подобрать нельзя: маршрута нет, считать крюк не от чего.
    if ((form.freeT || form.freeLengthM) && !form.toRegion) {
      bad.toRegion = 'Для догруза укажите, куда едете';
    }
    if (Object.keys(bad).length) return errs.fail(bad);

    errs.reset();
    setBusy(true);
    try {
      await api.addAvailability({
        vehicleId: form.vehicleId || null,
        fromRegion: form.fromRegion,
        toRegion: form.toRegion || null,
        bodyType: form.bodyType,
        capacityKg: Math.round(form.capacityT * 1000),
        availableFrom: new Date(form.availableFrom),
        availableTo: new Date(form.availableTo),
        note: form.note || null,
        // Координаты берём по опорному городу региона: без них подбор
        // «по пути» посчитать нечем, а вводить их руками никто не станет.
        ...(() => {
          const a = regionPoint(form.fromRegion);
          const b = form.toRegion ? regionPoint(form.toRegion) : null;
          return a && b
            ? { fromLat: a.lat, fromLng: a.lng, toLat: b.lat, toLng: b.lng }
            : {};
        })(),
        freeKg: form.freeT ? Math.round(Number(form.freeT) * 1000) : null,
        freeLengthM: form.freeLengthM ? Number(form.freeLengthM) : null,
        detourKm: Number(form.detourKm) || 50,
      });
      notify('success');
      setShowForm(false);
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(a: AvailabilityRow) {
    if (!(await confirm('Снять машину с публикации?'))) return;
    await api.removeAvailability(a.id);
    await load();
  }

  return (
    <>
      <h2>Свободные машины</h2>

      <div className="seg" style={{ marginBottom: 12 }}>
        <button type="button" className={tab === 'all' ? 'seg__btn seg__btn--on' : 'seg__btn'} onClick={() => setTab('all')}>
          Все машины
        </button>
        <button type="button" className={tab === 'mine' ? 'seg__btn seg__btn--on' : 'seg__btn'} onClick={() => setTab('mine')}>
          Мои
        </button>
        {/* Подбор — вкладка здесь, а не отдельный пункт меню: это тот же
            парк, только повёрнутый к вопросу «кого куда поставить». */}
        {managedCompany && (
          <button type="button" className={tab === 'match' ? 'seg__btn seg__btn--on' : 'seg__btn'} onClick={() => setTab('match')}>
            Подбор под парк
          </button>
        )}
      </div>

      {tab === 'match' && managedCompany && (
        <FleetMatch companyId={managedCompany.id} onOpenCargo={onOpenCargo} />
      )}

      {tab !== 'match' && (
        <>
      {!showForm && (
        <button style={{ marginBottom: 12 }} onClick={() => setShowForm(true)}>
          Объявить свободную машину
        </button>
      )}

      {showForm && (
        <div className="card">
          <h3>Машина освобождается</h3>
          <div className="meta" style={{ marginBottom: 12 }}>
            Заказчики увидят её и предложат обратную загрузку.
          </div>

          {me && vehicleOptions.length > 0 && (
            <div className="field">
              <label>{managedCompany ? 'Из парка компании' : 'Из моего парка'}</label>
              <select
                value={form.vehicleId}
                onChange={(e) => {
                  const v = vehicleOptions.find((x) => x.id === e.target.value);
                  setForm({
                    ...form,
                    vehicleId: e.target.value,
                    ...(v
                      ? {
                          bodyType: v.bodyType as BodyType,
                          capacityT: Math.round(v.capacityKg / 100) / 10,
                        }
                      : {}),
                  });
                }}
              >
                <option value="">Не выбрана</option>
                {vehicleOptions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.plate} · {BODY_TYPE_LABEL[v.bodyType as BodyType]}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="field-row">
            <div className="field">
              <label>Освобождается в регионе</label>
              <RegionPicker
                label="Регион освобождения"
                value={form.fromRegion}
                onChange={(v) => setForm({ ...form, fromRegion: v })}
              />
            </div>
            <div className={`field${errs.has('toRegion') ? ' invalid-group' : ''}`} ref={errs.group('toRegion').ref}>
              <label>Готов ехать в</label>
              <RegionPicker
                label="Готов ехать в регион"
                value={form.toRegion}
                onChange={(v) => setForm({ ...form, toRegion: v })}
                allowEmpty="Куда угодно"
              />
              <FieldError form={errs} name="toRegion" />
            </div>
          </div>

          <div className="field">
            <label>Кузов</label>
            <BodyTypeSelect
              value={form.bodyType}
              onChange={(v) => v && setForm({ ...form, bodyType: v })}
            />
          </div>

          <div className="field-row">
            <div className="field">
              <label>Грузоподъёмность, т</label>
              <NumberField
                negative={false}
                value={form.capacityT}
                onChange={(v) => setForm({ ...form, capacityT: v })}
              />
            </div>
            <div className="field">
              <label>Свободна с</label>
              <input
                type="date"
                value={form.availableFrom}
                onChange={(e) => setForm({ ...form, availableFrom: e.target.value })}
              />
            </div>
            <div className="field">
              <label>по</label>
              <input
                {...errs.field('availableTo')}
                type="date"
                value={form.availableTo}
                onChange={(e) => setForm({ ...form, availableTo: e.target.value })}
              />
              <FieldError form={errs} name="availableTo" />
            </div>
          </div>

          {/* Догруз: машина едет не пустой, но место осталось. Указав
              остаток, перевозчик получает подбор попутных грузов вдоль
              своего маршрута — ради этого поля и заводятся. */}
          <div className="field-row">
            <div className="field">
              <label>Свободно, т</label>
              <input
                inputMode="decimal"
                placeholder="не указано"
                value={form.freeT}
                onChange={(e) => setForm({ ...form, freeT: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Свободно, м</label>
              <input
                inputMode="decimal"
                placeholder="не указано"
                value={form.freeLengthM}
                onChange={(e) => setForm({ ...form, freeLengthM: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Готов на крюк, км</label>
              <input
                inputMode="numeric"
                value={form.detourKm}
                onChange={(e) => setForm({ ...form, detourKm: e.target.value })}
              />
            </div>
          </div>
          <div className="meta" style={{ marginBottom: 'var(--s3)' }}>
            Заполните остаток — и во вкладке «Найти груз» появится подбор
            попутных заявок вдоль вашего маршрута. Для этого нужно указать
            и «куда».
          </div>

          <div className="field">
            <label>Комментарий</label>
            <input
              placeholder="Например: выгружаюсь утром, готов взять обратку"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </div>

          <div className="col">
            <button disabled={busy} onClick={() => void submit()}>
              Опубликовать
            </button>
            <button className="secondary" onClick={() => setShowForm(false)}>
              Отмена
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="field-row">
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Регион</label>
            <RegionPicker label="Регион" value={fRegion} onChange={setFRegion} allowEmpty="Любой" />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Кузов</label>
            <BodyTypeOne value={fBody} onChange={setFBody} anyLabel="Любой кузов" />
          </div>
        </div>
      </div>

      {loading && <div className="empty">Загрузка…</div>}
      {!loading && items.length === 0 && (
        <div className="empty">
          {tab === 'mine'
            ? 'Вы не объявляли свободных машин.'
            : 'Свободных машин по этим условиям нет.'}
        </div>
      )}

      {items.map((a) => (
        <div key={a.id} className="card">
          <div className="row">
            <div className="route">
              {a.fromRegion} → {a.toRegion ?? 'куда угодно'}
            </div>
            {a.freeKg || a.freeLengthM ? (
              <span
                className="badge badge--accent"
                data-tip="Машина уже гружёная, но место осталось"
              >
                догруз
              </span>
            ) : null}
            {a.isMine && <span className="badge">моя</span>}
          </div>

          <div className="meta">
            {BODY_TYPE_LABEL[a.bodyType]} · {(a.capacityKg / 1000).toFixed(1)} т
            {a.plate && ` · ${a.plate}`}
          </div>
          {(a.freeKg || a.freeLengthM) && (
            <div className="meta">
              Свободно
              {a.freeKg ? ` ${(a.freeKg / 1000).toFixed(1)} т` : ''}
              {a.freeLengthM ? ` · ${a.freeLengthM} м` : ''} · крюк до{' '}
              {a.detourKm} км
            </div>
          )}

          <div className="meta">
            Свободна {new Date(a.availableFrom).toLocaleDateString('ru-RU')} —{' '}
            {new Date(a.availableTo).toLocaleDateString('ru-RU')}
          </div>

          {a.note && <div className="meta" style={{ marginTop: 4 }}>{a.note}</div>}

          <div className="divider" />

          <div className="meta">
            {a.driverName}
            {a.driverRatingCount > 0
              ? ` · ★ ${a.driverRating.toFixed(1)} (${a.driverRatingCount})`
              : ' · без оценок'}
            {` · рейсов ${a.driverOrdersDone}`}
          </div>

          {!a.isMine && (
            <div className="col" style={{ marginTop: 8 }}>
              {a.phone && (
                <a href={`tel:${a.phone}`}>
                  <button className="secondary">Позвонить {a.phone}</button>
                </a>
              )}
              {a.tgUsername && (
                <button
                  className="secondary"
                  onClick={() => openTelegram(`https://t.me/${a.tgUsername}`)}
                >
                  Написать в Telegram
                </button>
              )}
            </div>
          )}

          {a.isMine && (
            <button
              className="danger"
              style={{ marginTop: 'var(--s2)' }}
              onClick={() => void remove(a)}
            >
              Снять с публикации
            </button>
          )}
        </div>
      ))}
        </>
      )}
    </>
  );
}
